const DEFAULT_CONFIG = {
  schema_version: '1',
  algorithm_version: '1',
  deal: {
    customer_vat_rate: '0.22',
    profit_reserve_rate: '0.25',
    tax_fund_rate: '0.02',
    min_deal_result: '2500',
    kpi_other_client_rate: '0.01',
    kpi_inbev_high_rate: '0.02',
    kpi_high_profitability_threshold: '0.05',
    manager_salary_rates: ['0.17', '0.20'],
    carrier_vat_rates: ['0.05', '0.07', '0.22'],
  },
  strategies: [],
  subsidy: {
    target_annual_yield: '0',
    business_to_calendar_days_factor: '1',
    documents_extra_calendar_days: 0,
    max_discount: '0',
    discount_round_step: '0.0001',
    prepayment_reference_discount: '0',
    prepayment_reference_base_days: 0,
    prepayment_reference_term_days: 0,
    prepayment_reference_advance_share: '0',
    curve: [],
  },
  clients: [],
  payment_terms: [],
}

export function cloneConfig(value) {
  return JSON.parse(JSON.stringify(value))
}

export function unwrapApiObject(payload) {
  let current = payload
  const wrapperKeys = ['data', 'result', 'item', 'draft', 'version_data']
  for (let depth = 0; depth < 5; depth += 1) {
    if (!current || typeof current !== 'object' || Array.isArray(current)) break
    const wrapperKey = wrapperKeys.find((key) => current[key] && typeof current[key] === 'object' && !Array.isArray(current[key]))
    if (!wrapperKey || current.config || current.id || current.version || current.active_version) break
    current = current[wrapperKey]
  }
  return current || {}
}

export function normalizeEditableConfig(payload) {
  const root = unwrapApiObject(payload)
  const source = root.config || root.configuration || root
  const deal = source.deal || {}
  const subsidy = source.subsidy || {}
  return {
    ...cloneConfig(DEFAULT_CONFIG),
    ...cloneConfig(source),
    deal: { ...cloneConfig(DEFAULT_CONFIG.deal), ...cloneConfig(deal) },
    subsidy: { ...cloneConfig(DEFAULT_CONFIG.subsidy), ...cloneConfig(subsidy), curve: cloneConfig(subsidy.curve || []) },
    strategies: cloneConfig(source.strategies || []),
    clients: cloneConfig(source.clients || []),
    payment_terms: cloneConfig(source.payment_terms || []),
  }
}

export function normalizeConfigEnvelope(payload) {
  const root = unwrapApiObject(payload)
  return {
    ...root,
    id: root.id ?? root.draft_id ?? null,
    version: root.version ?? root.config_version ?? root.active_version ?? null,
    active_version: root.active_version ?? root.version ?? root.config_version ?? null,
    updated_at: root.updated_at ?? root.draft_updated_at ?? null,
    status: root.status ?? root.draft_status ?? null,
    config: normalizeEditableConfig(root),
    derived_adjusted_discounts: root.derived_adjusted_discounts || root.derived?.adjusted_discounts || [],
  }
}

export function normalizeHistory(payload) {
  if (Array.isArray(payload)) return payload
  const root = unwrapApiObject(payload)
  const values = root.items || root.history || root.versions || root.data || []
  return Array.isArray(values) ? values : []
}

export function normalizeDrafts(payload) {
  if (Array.isArray(payload)) return payload
  const root = unwrapApiObject(payload)
  const values = root.items || root.drafts || root.data || []
  return Array.isArray(values) ? values : []
}

export function isDraftValidated(draft) {
  return Boolean(draft?.checksum && draft?.validated_checksum && draft.checksum === draft.validated_checksum)
}

export function normalizePreviewDraftTarget(target, currentDraft) {
  const hasExplicitId = target && typeof target === 'object' && !Array.isArray(target)
    && Object.prototype.hasOwnProperty.call(target, 'id')
  const candidate = hasExplicitId ? target : currentDraft
  const id = candidate?.id
  const hasValidId = (typeof id === 'string' && id.trim() !== '')
    || (typeof id === 'number' && Number.isFinite(id))
  return hasValidId ? candidate : null
}

const REPORT_LABELS = {
  total_scenarios: 'Проверено сценариев',
  scenario_count: 'Проверено сценариев',
  valid: 'Успешно',
  invalid: 'С ошибками',
  errors: 'Ошибки',
  warnings: 'Предупреждения',
  changed: 'Изменено',
  unchanged: 'Без изменений',
  added: 'Добавлено',
  removed: 'Удалено',
  active: 'активная версия',
  draft: 'черновик',
  delta: 'Δ',
}

function reportLabel(value) {
  return REPORT_LABELS[value] || String(value).replaceAll('_', ' ')
}

function reportValue(value) {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'да' : 'нет'
  if (typeof value === 'number') return value.toLocaleString('ru-RU')
  if (Array.isArray(value)) return value.map(reportValue).join(', ')
  if (typeof value === 'object') return formatReportGroup(value)
  return String(value)
}

function formatReportGroup(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return reportValue(value)
  return Object.entries(value)
    .map(([key, item]) => `${reportLabel(key)}: ${reportValue(item)}`)
    .join(', ')
}

function formatValidationError(value) {
  if (typeof value === 'string') return value
  if (!value || typeof value !== 'object') return reportValue(value)
  const location = value.path || value.field || value.loc
  const message = value.message || value.msg || value.error || 'Ошибка проверки'
  const normalizedLocation = Array.isArray(location) ? location.join(' → ') : location
  return normalizedLocation ? `${normalizedLocation}: ${message}` : String(message)
}

export function formatValidationReport(payload) {
  const root = unwrapApiObject(payload)
  const report = root.validation_report && typeof root.validation_report === 'object' ? root.validation_report : {}
  const messages = []
  const scenarioCount = report.total_scenarios ?? report.scenario_count ?? root.total_scenarios ?? root.scenario_count
  if (scenarioCount !== undefined && scenarioCount !== null) messages.push(`Проверено сценариев: ${reportValue(scenarioCount)}`)

  const categoryCounts = report.category_counts || report.categories || report.counts
  if (categoryCounts && typeof categoryCounts === 'object') {
    messages.push(`По категориям: ${formatReportGroup(categoryCounts)}`)
  }

  const comparison = report.comparison
  if (comparison && typeof comparison === 'object') {
    if (comparison.scenario) messages.push(`Сравнение: ${reportValue(comparison.scenario)}`)
    if (comparison.delta && typeof comparison.delta === 'object') {
      messages.push(`Изменение результата: ${formatReportGroup(comparison.delta)}`)
    }
  }

  const errors = root.errors || root.messages || root.validation_errors || report.errors || []
  const errorItems = Array.isArray(errors) ? errors : [errors]
  errorItems.filter((item) => item !== null && item !== undefined && item !== '').forEach((item) => {
    messages.push(`Ошибка: ${formatValidationError(item)}`)
  })
  if (!messages.length) messages.push(root.valid === false ? 'Конфигурация содержит ошибки' : 'Ошибок не найдено')
  else if (root.valid !== false && !errorItems.length) messages.push('Ошибок не найдено')
  return messages
}

export function buildCalculatorRequest(form, configVersion) {
  const request = { ...form }
  delete request.config_version
  if (configVersion) {
    const version = Number(configVersion)
    request.config_version = Number.isFinite(version) ? version : configVersion
  }
  return request
}

export function toConfigPayload(config) {
  const value = normalizeEditableConfig(config)
  const integer = (item) => Number.isFinite(Number(item)) ? Number(item) : 0
  return {
    ...value,
    schema_version: value.schema_version,
    algorithm_version: String(value.algorithm_version),
    deal: {
      ...value.deal,
      manager_salary_rates: value.deal.manager_salary_rates.map(String),
      carrier_vat_rates: value.deal.carrier_vat_rates.map(String),
    },
    strategies: value.strategies.map((item, index) => ({ ...item, order: integer(item.order ?? index) })),
    subsidy: {
      ...value.subsidy,
      documents_extra_calendar_days: integer(value.subsidy.documents_extra_calendar_days),
      prepayment_reference_base_days: integer(value.subsidy.prepayment_reference_base_days),
      prepayment_reference_term_days: integer(value.subsidy.prepayment_reference_term_days),
      curve: value.subsidy.curve.map((item) => ({ ...item, days: integer(item.days) })),
    },
    clients: value.clients.map((item, index) => ({
      ...item,
      base_business_days: integer(item.base_business_days),
      active: Boolean(item.active),
      order: integer(item.order ?? index),
    })),
    payment_terms: value.payment_terms.map((item, index) => ({
      ...item,
      business_days: integer(item.business_days),
      active: Boolean(item.active),
      order: integer(item.order ?? index),
    })),
  }
}

export function normalizeDerivedMatrix(value) {
  if (Array.isArray(value)) return value
  if (!value || typeof value !== 'object') return []
  return Object.entries(value).map(([key, row]) => (
    row && typeof row === 'object' && !Array.isArray(row) ? { key, ...row } : { key, value: row }
  ))
}

export function normalizeVisibleOrder(items) {
  return items.map((item, index) => {
    item.order = index
    return item
  })
}

export function formatDerivedColumnLabel(key) {
  if (key === 'key') return 'Стратегия'
  if (key === 'without_vat') return 'Без НДС'
  if (/^0(?:\.\d+)?$/.test(String(key))) {
    const percent = String(Math.round(Number(key) * 1e12) / 1e10).replace('.', ',')
    return `НДС ${percent}%`
  }
  return String(key ?? '—')
}

export function formatDerivedStrategyLabel(key, strategies = []) {
  const strategy = strategies.find((item) => String(item?.key) === String(key))
  return strategy?.name || String(key ?? '—')
}

export function sortHistoryLatestFirst(items) {
  return [...items].sort((left, right) => {
    const versionDifference = Number(right?.version ?? right?.config_version ?? right?.id) - Number(left?.version ?? left?.config_version ?? left?.id)
    if (Number.isFinite(versionDifference) && versionDifference !== 0) return versionDifference
    const rightDate = Date.parse(right?.published_at || right?.created_at || right?.updated_at || '') || 0
    const leftDate = Date.parse(left?.published_at || left?.created_at || left?.updated_at || '') || 0
    return rightDate - leftDate
  })
}

export function formatApiErrorDetail(detail, fallback = 'Не удалось выполнить операцию') {
  if (detail === null || detail === undefined || detail === '') return fallback
  if (typeof detail === 'string' || typeof detail === 'number' || typeof detail === 'boolean') return String(detail)
  if (Array.isArray(detail)) {
    const messages = detail.map((item) => formatApiErrorDetail(item, '')).filter(Boolean)
    return messages.length ? messages.join('; ') : fallback
  }
  if (typeof detail === 'object') {
    const message = detail.message || detail.msg || detail.error
    if (message) return formatApiErrorDetail(message, fallback)
    try {
      return JSON.stringify(detail)
    } catch (error) {
      return fallback
    }
  }
  return fallback
}
